import * as ReactDeviceDetect from "react-device-detect";
import { Logger } from "./Logger";
import {isFeatureEnabled} from "../services/OptimizelyService";

export enum DeviceSupportedStatuses {
  // Browser is too old or does not support getUserMedia
  GET_USER_MEDIA_MISSING = "GET_USER_MEDIA_MISSING",
  // Browser does not have getUserMedia but should
  GET_USER_MEDIA_MISSING_POSSIBLY_EMBEDDED = "GET_USER_MEDIA_MISSING_POSSIBLY_EMBEDDED",
  // Browser seems to support getUserMedia
  GOOD_TO_GO = "GOOD_TO_GO",
  // Chrome or FireFox on iOS
  IPHONE_BUT_NOT_SAFARI = "IPHONE_BUT_NOT_SAFARI",
  // Capture being viewed through 3rd app such a messages app on iOS
  IPHONE_EMBEDDED_SAFARI = "IPHONE_EMBEDDED_SAFARI",
  // Android mobile application - force manual upload
  ANDROID_APPLICATION_FORCE_MANUAL = "ANDROID_APPLICATION_FORCE_MANUAL",
  // IOS mobile application -force manual upload
  IOS_APPLICATION_FORCE_MANUAL = "IOS_APPLICATION_FORCE_MANUAL",
}

export class DeviceDetection {
  static MINIMUM_IOS_VERSION_FOR_VERIFF = "2.180.4";
  public static getTags = (localDecodeAttempts: number | undefined, manualUpload: string | undefined): string[] => {
    const tags = Array<string> ();
    tags.push(`deviceOsName:${ReactDeviceDetect.osName}`);
    tags.push(`deviceOsVersion:${ReactDeviceDetect.osVersion}`);
    tags.push(`deviceBrowserName:${ReactDeviceDetect.browserName}`);
    tags.push(`deviceBrowserVersion:${ReactDeviceDetect.browserVersion}`);
    tags.push(`mobileModel:${ReactDeviceDetect.mobileModel}`);
    tags.push(`mobileVendor:${ReactDeviceDetect.mobileVendor}`);

    if (localDecodeAttempts != null) {
      tags.push(`localDecodeAttempts:${localDecodeAttempts}`);
    }

    if (manualUpload != null) {
      tags.push(`manualUpload:${manualUpload}`);
    }

    return tags;
  };
  public static getDeviceTags = (): object => {
    return {
      "deviceOsName" : ReactDeviceDetect.osName,
      "deviceOsVersion": ReactDeviceDetect.osVersion,
      "deviceBrowserName": ReactDeviceDetect.browserName,
      "deviceBrowserVersion": ReactDeviceDetect.browserVersion,
      "mobileModel": ReactDeviceDetect.mobileModel,
      "mobileVendor": ReactDeviceDetect.mobileVendor,
      "isSofiApp": DeviceDetection.isSofiApp()
    };
  };

  public static getDataDogDeviceTags = (): string => {
    return ReactDeviceDetect.isMobile? (DeviceDetection.isSofiApp()?
            `${ReactDeviceDetect.osName} app`
            : ReactDeviceDetect.osName)
        : "desktop";
  };

  private static isSofiApp = (): boolean => {
    return navigator.userAgent.includes("SOFI_APP_ID=sofi-android") ||
        navigator.userAgent.includes("SOFI_APP_ID=sofi-ios");
  };

  public static webWorkersSupported = (): boolean => {
    return typeof(Worker) !== "undefined";
  };

  public static getUserMediaFn(nav: any): ((constraints: MediaStreamConstraints) => Promise<MediaStream>) | null {
    let media = nav.getUserMedia || nav.webkitGetUserMedia || nav.mozGetUserMedia;
    if (!media && nav.mediaDevices) {
      media = nav.mediaDevices.getUserMedia;
    }
    return media;
  }

  private static async isVeriffEnabledForAndroid() {
    return await isFeatureEnabled('use_veriff_for_android')
        .catch((err) => {
          // if it fails getting result from optimizely, isEnabled will be treated as false
          Logger.error(`failed fetching feature flag from optimizely: ${err.message}`);
        });
  }

  /**
   * Checks if the inputVersion is equal to or greater than minimumValidVersion.
   * Note: If the input version is a malformed string not having 3 segments (i.e 2.181), function will return false.
   * There is a mobile app bug which causes some versions to be set to 1 - allow veriff for those as well.
   * @param inputVersion SoFi app version that the user is on.
   * @param minimumValidVersion user must on a version greater than this
   * @private
   */
  private static meetsMinimumVersionRequirement(inputVersion: string, minimumValidVersion: string) {
    const inputVersionArray: number[] = inputVersion.split(".").map(Number);
    const minimumVersionArray: number[] = minimumValidVersion.split(".").map(Number);
    if (inputVersionArray.length !== minimumVersionArray.length) {
      if (inputVersionArray.length === 1 && inputVersionArray[0] === 1) {
        return true;
      }
      Logger.error(`Malformed string: [${inputVersion}] sent for app version comparison.`);
      return false;
    }
    for (let i = 0; i < minimumVersionArray.length; i++) {
      if (inputVersionArray[i] > minimumVersionArray[i]) {
        return true;
      } else if (inputVersionArray[i] < minimumVersionArray[i]) {
        return false;
      }
    }
    return true;
  }

  /**
   * Function checks if feature flag is enabled for Veriff for iOS and if user's SoFi app version
   * meets the minimum valid app version.
   * @param nav user agent which has app version details
   * @private
   */
  private static async allowVeriffForIOS(nav: any) {
    const veriffFeatureFlag : boolean = await isFeatureEnabled('veriff-for-ios')
        .catch((err) => {
          Logger.error(`Failed to fetch feature flag veriff-for-ios from Optimizely: ${err.message}`);
          return false;
        });
    if (!veriffFeatureFlag) {
      return false;
    }
    const matchedVersionReg = RegExp(`SOFI_APP_VERSION=([0-9.]*)`);
    const matchedVersion = matchedVersionReg.exec(nav.userAgent);
    if (matchedVersion != null && matchedVersion.length > 1) {
      // Allow Veriff if user's SoFi App version is greater than 2.180.3
      return this.meetsMinimumVersionRequirement(matchedVersion[1], this.MINIMUM_IOS_VERSION_FOR_VERIFF);
    }
    return false;
  }

  public static async getDeviceSupportedStatus(nav: any): Promise<DeviceSupportedStatuses> {
    if (nav.userAgent.includes("SOFI_APP_ID=sofi-android") && !(await DeviceDetection.isVeriffEnabledForAndroid())) {
      return DeviceSupportedStatuses.ANDROID_APPLICATION_FORCE_MANUAL;
    }

    if (nav.userAgent.includes("SOFI_APP_ID=sofi-ios")) {
      if (!await this.allowVeriffForIOS(nav)) {
        return DeviceSupportedStatuses.IOS_APPLICATION_FORCE_MANUAL;
      }
    }

    if (!!this.getUserMediaFn(nav)) {
      return DeviceSupportedStatuses.GOOD_TO_GO;
    }

    // determine why
    const browserName = ReactDeviceDetect.browserName.toLowerCase();
    const browserVersion = Number(ReactDeviceDetect.browserVersion);

    if (ReactDeviceDetect.isMobile && ReactDeviceDetect.isIOS && browserName !== "mobile safari") {
      if (browserName === "mobile safari uiwebview") {
        return DeviceSupportedStatuses.IPHONE_EMBEDDED_SAFARI;
      } else {
        return DeviceSupportedStatuses.IPHONE_BUT_NOT_SAFARI;
      }
    }

    // Log some info if version should be good so we can track this down. (Rollbar should have all device info already)
    let shouldSupport = false;
    if (ReactDeviceDetect.isMobileSafari && browserVersion >= 11) {
      return DeviceSupportedStatuses.GET_USER_MEDIA_MISSING_POSSIBLY_EMBEDDED;
    } else if (ReactDeviceDetect.isAndroid) {
      if (browserName === "firefox mobile" && browserVersion >= 36) {
        shouldSupport = true;
      } else if (browserName === "chrome mobile" && browserVersion >= 52) {
        shouldSupport = true;
      } else {
        // Can add browsers to ignore as they pop up
        shouldSupport = true;
      }
    }
    if (shouldSupport) {
      Logger.warn(`GetUserMedia is undefined for ${browserName}:${browserVersion}, but should not be`);
    }
    return DeviceSupportedStatuses.GET_USER_MEDIA_MISSING;
  }
}



// WEBPACK FOOTER //
// ./src/constants/DeviceDetection.ts